import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type Report } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter, toTimestampString } from './firestoreUtils.js';
import type { ReportRepository } from '@/infrastructure/repositories/RepositoryInterfaces.js';

const REPORTS = 'reports';

const converter = buildConverter<Report>(
  (data) =>
    ({
      ...data,
      createdAt: toTimestampString(data.createdAt) ?? new Date().toISOString(),
      updatedAt: toTimestampString(data.updatedAt) ?? new Date().toISOString(),
    }) as Report,
  (model) => ({ ...model, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
);

export class FirestoreReportRepository implements ReportRepository {
  async findAll(): Promise<Report[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, REPORTS),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('createdAt', 'desc'),
      limit(100)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findById(id: string): Promise<Report | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, REPORTS, id).withConverter(converter));
    return snap.data();
  }

  async findByProjectId(projectId: string): Promise<Report[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, REPORTS),
      where('projectId', '==', projectId),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('createdAt', 'desc'),
      limit(100)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findBySimulationRunId(simulationRunId: string): Promise<Report[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, REPORTS),
      where('simulationRunId', '==', simulationRunId),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('createdAt', 'desc'),
      limit(100)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(report: Report): Promise<Report> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const data = { ...report, environment: getCurrentEnvironment() };
    await setDoc(doc(db, REPORTS, report.id).withConverter(converter), data);
    return data;
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, REPORTS, id));
  }
}

export const createFirestoreReportRepository = (): FirestoreReportRepository => new FirestoreReportRepository();
